import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { extractErrorMessage } from '@/pages/painel-pedidos/lib/errors';

interface Props {
  password: string;
}

interface Resumo {
  kpis: { reativaveis: number; fornecedores: number; arquitetos: number; orcamentos_sem_fechamento: number };
}

export default function InteligenciaResumo({ password }: Props) {
  const [data, setData] = useState<Resumo | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let ativo = true;
    (async () => {
      try {
        const { data: resp, error: invokeErr } = await supabase.functions.invoke('filas-admin', {
          body: { action: 'inteligencia_comercial', password },
        });
        if (invokeErr) throw new Error(await extractErrorMessage(invokeErr));
        if (resp?.error) throw new Error(resp.error);
        if (ativo) setData(resp as Resumo);
      } catch (e) {
        if (ativo) setError(e instanceof Error ? e.message : 'Erro ao carregar inteligência.');
      }
    })();
    return () => { ativo = false; };
  }, [password]);

  const cards = [
    {
      label: 'Reativações recomendadas',
      value: data?.kpis.reativaveis ?? 0,
      icon: 'ri-restart-line',
      tone: 'bg-emerald-50 text-emerald-700',
      to: '/painel/crm/filas',
    },
    {
      label: 'Fornecedores a consultar',
      value: data?.kpis.fornecedores ?? 0,
      icon: 'ri-store-3-line',
      tone: 'bg-cyan-50 text-cyan-700',
      to: '/painel/fornecedores',
    },
    {
      label: 'Orçamentos sem fechamento',
      value: data?.kpis.orcamentos_sem_fechamento ?? 0,
      icon: 'ri-file-list-3-line',
      tone: 'bg-amber-50 text-amber-700',
      to: '/painel/crm/filas',
    },
  ];

  return (
    <section className="bg-white rounded-2xl border border-gray-200 p-5">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-orange-50 text-orange-600">
            <i className="ri-brain-line"></i>
          </div>
          <h3 className="font-bold text-gray-900">Inteligência em ação</h3>
        </div>
        <Link to="/painel/crm/filas" className="text-xs font-semibold text-[#E8610A] hover:text-[#C4500A] cursor-pointer whitespace-nowrap">
          Ver filas
          <i className="ri-arrow-right-line ml-1"></i>
        </Link>
      </div>

      {error ? (
        <p className="text-xs text-gray-400">Inteligência indisponível no momento.</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {cards.map((c) => (
            <Link key={c.label} to={c.to} className="bg-gray-50 rounded-xl p-4 hover:bg-gray-100 transition-colors cursor-pointer">
              <div className="flex items-center gap-2.5">
                <div className={`w-9 h-9 flex items-center justify-center rounded-lg ${c.tone}`}>
                  <i className={c.icon}></i>
                </div>
                <div className="min-w-0">
                  <p className="text-[11px] text-gray-500 uppercase tracking-wider font-semibold leading-tight">{c.label}</p>
                  <p className="text-lg font-bold text-gray-900">{c.value}</p>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </section>
  );
}